import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Give up after a handful of frames: enough to ride out the layout settling,
 * short enough that it never fights a visitor who starts scrolling themselves.
 */
const SETTLE_FRAMES = 10;

/**
 * Navigate to a landing section by its DOM id.
 *
 * React Router navigates through the History API, and browsers do not perform
 * their native anchor jump for those — so a plain `<Link to="/#tarifs">` only
 * rewrites the address bar. Every in-page jump therefore has to scroll itself.
 */
export const useSectionNavigation = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return useCallback(
    (id: string) => {
      if (pathname !== "/") {
        // Not on the landing page: route to it and let useHashScroll take over
        // once the sections have mounted.
        navigate(`/#${id}`);
        return;
      }

      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      // Keep the address bar in step, but bypass the router: a router
      // navigation would re-run useHashScroll and jump on top of this
      // smooth scroll.
      window.history.replaceState(null, "", `/#${id}`);
    },
    [navigate, pathname]
  );
};

/** Scrolls back to the top of the page and drops any section fragment. */
export const useScrollToTop = () => {
  const { pathname } = useLocation();

  return useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (pathname === "/") {
      window.history.replaceState(null, "", "/");
    }
  }, [pathname]);
};

/**
 * Honours the URL fragment on the landing page — both for deep links such as
 * `/#tarifs` and for arrivals from another route. Sections below the fold move
 * while fonts, lazy images and the pricing request settle, so the target is
 * re-pinned each frame until its position stops changing.
 */
export const useHashScroll = () => {
  const { hash } = useLocation();

  useEffect(() => {
    const id = hash.replace("#", "");
    if (!id) return undefined;

    let frame = 0;
    let previousTop: number | null = null;
    let request = 0;

    const pin = () => {
      const target = document.getElementById(id);
      if (target) {
        const { top } = target.getBoundingClientRect();
        target.scrollIntoView({ block: "start" });
        if (previousTop !== null && Math.abs(top - previousTop) < 1) return;
        previousTop = top;
      }
      frame += 1;
      if (frame < SETTLE_FRAMES) request = requestAnimationFrame(pin);
    };

    request = requestAnimationFrame(pin);
    return () => cancelAnimationFrame(request);
  }, [hash]);
};
